import {forwardRef, useCallback, useImperativeHandle, useState} from 'react'
import Card from '_core/components/Card'
import paymentService from 'app/services/PaymentService'
import {getPaymentMethods} from 'app/features/customers/redux/paymentMethodSlice'
import {useDispatch} from 'react-redux'
import {showMessage} from 'app/redux/messageSlice'
import clsx from 'clsx'
import CardForm from './CardForm'
import _ from 'lodash'

type Props = {
  children: React.ReactNode
  showCard?: boolean
  className?: any
  config: any
}

const PayEngine = forwardRef((props: Props, ref: any) => {
  const dispatch = useDispatch()
  const [payEngineForm, setPayEngineForm] = useState<any>()
  const [validatePaymentError, setValidatePaymentError] = useState({})
  const {children, showCard, className, config} = props

  const handlePayEngineLoaded = (payEngineForm: any) => {
    setPayEngineForm(payEngineForm)
  }

  useImperativeHandle(ref, () => ({
    createPaymentMethod: handleCreatePaymentModel,
    getPayEngineForm: () => {
      return payEngineForm
    },
  }))

  const handleCreatePaymentModel = useCallback(
    async (address: any, customerId: string, isPrimary: boolean) => {
      const payEngineForm = ref.current.getPayEngineForm()
      if (payEngineForm) {
        try {
          setValidatePaymentError({})
          const card = await payEngineForm.createCard({
            card_holder: `${address.firstName} ${address.lastName}`,
            merchant_id: config.merchantId,
            address_line1: address.addressLine1,
            address_zip: address.zipCode,
            address_city: address.city,
          })
          const model = mapToPaymentMethodModel(address.id, customerId, card, isPrimary)
          const out = await paymentService.createCardPaymentMethod(model)
          dispatch(getPaymentMethods(customerId))
          dispatch(
            showMessage({
              text: 'Successfully created payment card.',
              variant: 'success',
            })
          )
          return out
        } catch (error: any) {
          console.error(error)
          if (error.data && error.data.error) {
            return {error: error.data.message}
          } else {
            validatePayEnginePaymentFields(error, setValidatePaymentError)
          }
          return {}
        }
      }
    },
    [dispatch]
  )

  if (showCard) {
    return (
      <Card title='Credit Card Information'>
        <CardForm
          onPayEngineLoaded={handlePayEngineLoaded}
          errors={validatePaymentError}
          config={config}
        />
        {children && <div className='mb-5'>{children}</div>}
      </Card>
    )
  }

  return (
    <div className={(clsx('mx-5'), className)}>
      <div className='fs-4 fw-bolder text-dark mb-3 py-4'>Credit Card Information</div>
      <CardForm
        onPayEngineLoaded={handlePayEngineLoaded}
        errors={validatePaymentError}
        config={config}
      />

      {children && <div className='mb-5'>{children}</div>}
    </div>
  )
})

export default PayEngine

const mapToPaymentMethodModel = (
  addressId: string,
  customerId: string,
  paymentMethod: any,
  isPrimary: boolean
) => {
  const {token, card_data: card} = paymentMethod
  const pm: any = {
    externalId: token,
    maskedNumber: card.last_4,
    brand: card.brand,
    expMonth: card.exp_month.toString(),
    expYear: card.exp_year.toString(),
    addressId,
    customerId,
    isPrimary,
  }

  return pm
}

const validatePayEnginePaymentFields = (cardObj: any, setErrors: any) => {
  const errors = {}
  _.forIn(cardObj, (value, key) => {
    _.set(errors, key, value.errorMessages[0])
  })

  setErrors(errors)
}
